import { useState, useEffect } from 'react';
import { resumeApi, tailorApi, type Resume, type TailorResponse } from '@src/api/client';

const TailorPage = () => {
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [selectedResume, setSelectedResume] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [result, setResult] = useState<TailorResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    resumeApi
      .list()
      .then(data => {
        const list = data || [];
        setResumes(list);
        const def = list.find(r => r.isDefault);
        if (def) setSelectedResume(def.id);
        else if (list.length > 0) setSelectedResume(list[0].id);
      })
      .catch(() => setError('Failed to load resumes'));
  }, []);

  const handleTailor = async () => {
    if (!selectedResume || !jobDescription.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await tailorApi.tailor({ resumeId: selectedResume, jobDescription });
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Tailoring failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!jobDescription.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await tailorApi.analyze({ jobDescription, resumeId: selectedResume || undefined });
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Tailor Resume</h1>
        <p className="text-gray-500">AI-optimize your resume for a specific job</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-xl bg-white p-6 shadow-sm">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Resume</label>
                <select
                  value={selectedResume}
                  onChange={e => setSelectedResume(e.target.value)}
                  className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1">
                  <option value="">Select a resume</option>
                  {resumes.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                      {r.isDefault ? ' (Default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Job Description</label>
                <textarea
                  value={jobDescription}
                  onChange={e => setJobDescription(e.target.value)}
                  rows={12}
                  placeholder="Paste the full job description here..."
                  className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleTailor}
                  disabled={loading || !selectedResume || !jobDescription.trim()}
                  className="bg-brand-500 hover:bg-brand-600 flex-1 rounded-lg px-4 py-2.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50">
                  {loading ? 'Processing...' : 'Tailor Resume'}
                </button>
                <button
                  onClick={handleAnalyze}
                  disabled={loading || !jobDescription.trim()}
                  className="rounded-lg border px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                  Analyze Only
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {error && <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700">{error}</div>}

          {result && (
            <>
              <div className="rounded-xl bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Match Score</h2>
                  <span
                    className={`text-2xl font-bold ${
                      result.matchScore >= 80
                        ? 'text-green-600'
                        : result.matchScore >= 60
                          ? 'text-yellow-600'
                          : 'text-red-600'
                    }`}>
                    {Math.round(result.matchScore)}%
                  </span>
                </div>
                {result.matchNotes && <p className="text-sm text-gray-600">{result.matchNotes}</p>}
                {result.missingSkills?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700">Missing Skills:</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {result.missingSkills.map(s => (
                        <span key={s} className="rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-700">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {result.tailoredResume && (
                <div className="rounded-xl bg-white p-6 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Tailored Resume</h2>
                    <button
                      onClick={() => navigator.clipboard.writeText(result.tailoredResume)}
                      className="rounded-lg border px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
                      Copy
                    </button>
                  </div>
                  <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-sm">
                    {result.tailoredResume}
                  </pre>
                </div>
              )}

              {result.coverLetter && (
                <div className="rounded-xl bg-white p-6 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Cover Letter</h2>
                    <button
                      onClick={() => navigator.clipboard.writeText(result.coverLetter)}
                      className="rounded-lg border px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
                      Copy
                    </button>
                  </div>
                  <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-sm">
                    {result.coverLetter}
                  </pre>
                </div>
              )}
            </>
          )}

          {!result && !error && (
            <div className="rounded-xl bg-gray-50 p-12 text-center">
              <p className="text-gray-400">
                Paste a job description and click "Tailor Resume" to get AI-optimized results
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TailorPage;
